import React, { useState, useEffect } from "react";
import MultipleEmailForm from "./MultipleEmailForm";
import { MultiSelect } from "react-multi-select-component";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { base_url } from "../../Global";
import { render } from "@react-email/render";
import {
  collection,
  getDocs,
  addDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db, loginAdmin } from "../../Firebase";
import {
  Html,
  Head,
  Body,
  Container,
  Section,
  Heading,
  Text,
  Button,
  Preview,
  Img,
  Hr,
  Row,
  Column,
} from "@react-email/components";

const LegacyXTemplate = ({ blocks, globalSettings }) => {
  const main = {
    backgroundColor: globalSettings.pageColor,
    fontFamily: globalSettings.fontFamily,
  };

  const container = {
    backgroundColor: globalSettings.containerColor,
    margin: "0 auto",
    padding: "0px 0 0px",
    borderRadius: "12px",
    maxWidth: `${globalSettings.containerWidth || 600}px`,
    border: `1px solid ${globalSettings.borderColor}`,
    overflow: "hidden",
  };

  const styles = {
    heading: {
      lineHeight: "1.2",
    },
    text: {
      lineHeight: "1.5",
      whiteSpace: "pre-wrap",
    },
    image: {
      width: "100%",
      objectFit: "cover",
      backgroundColor: "#2a0a4a",
      display: "block",
    },
    button: {
      borderRadius: "6px",
      textDecoration: "none",
      display: "inline-block",
      padding: "12px 24px",
    },
    divider: {
      borderTop: `1px solid ${globalSettings.borderColor}`,
      width: "100%",
    },
  };

  const getSectionStyle = (block) => {
    let paddingObj = { padding: "0px", margin: "0px" };

    if (!(block.type === "image" && block.isHero)) {
      const pt = block.paddingTop !== undefined ? block.paddingTop : 0;
      const pb = block.paddingBottom !== undefined ? block.paddingBottom : 0;
      paddingObj = { padding: `${pt}px 32px ${pb}px 32px`, margin: "0px" };
    }

    let backgroundObj = {};
    if (block.useBgImage && block.bgImage) {
      backgroundObj = {
        backgroundImage: `url(${block.bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundColor: block.fallbackBgColor || "transparent",
      };
    } else if (block.fallbackBgColor) {
      backgroundObj = {
        backgroundColor: block.fallbackBgColor,
      };
    }

    return { ...paddingObj, ...backgroundObj };
  };

  const renderTextContent = (text) => {
    if (!text) return null;
    return text.split("\n").map((line, i, arr) => (
      <span key={i}>
        {line}
        {i !== arr.length - 1 && <br />}
      </span>
    ));
  };

  const renderInnerElement = (element, isColumn = false) => {
    if (!element || element.type === "empty") return null;

    if (element.type === "heading") {
      return (
        <Heading
          style={{
            ...styles.heading,
            textAlign: element.align || "left",
            color: element.color || globalSettings.headingColor,
            fontSize: element.fontSize ? `${element.fontSize}px` : "24px",
            fontWeight: element.isBold !== false ? "bold" : "normal",
            fontStyle: element.isItalic ? "italic" : "normal",
            textDecoration: element.isUnderline ? "underline" : "none",
            marginTop:
              element.marginTop !== undefined
                ? `${element.marginTop}px`
                : "0px",
            marginBottom:
              element.marginBottom !== undefined
                ? `${element.marginBottom}px`
                : "0px",
            marginLeft:
              element.marginLeft !== undefined
                ? `${element.marginLeft}px`
                : "0px",
            marginRight:
              element.marginRight !== undefined
                ? `${element.marginRight}px`
                : "0px",
            paddingTop:
              element.innerPaddingTop !== undefined
                ? `${element.innerPaddingTop}px`
                : "0px",
            paddingBottom:
              element.innerPaddingBottom !== undefined
                ? `${element.innerPaddingBottom}px`
                : "0px",
            paddingLeft:
              element.innerPaddingLeft !== undefined
                ? `${element.innerPaddingLeft}px`
                : "0px",
            paddingRight:
              element.innerPaddingRight !== undefined
                ? `${element.innerPaddingRight}px`
                : "0px",
          }}
        >
          {renderTextContent(element.text)}
        </Heading>
      );
    }
    if (element.type === "text") {
      return (
        <Text
          style={{
            ...styles.text,
            textAlign: element.align || "left",
            color: element.color || globalSettings.textColor,
            fontSize: element.fontSize ? `${element.fontSize}px` : "15px",
            fontWeight: element.isBold ? "bold" : "normal",
            fontStyle: element.isItalic ? "italic" : "normal",
            textDecoration: element.isUnderline ? "underline" : "none",
            marginTop:
              element.marginTop !== undefined
                ? `${element.marginTop}px`
                : "0px",
            marginBottom:
              element.marginBottom !== undefined
                ? `${element.marginBottom}px`
                : "0px",
            marginLeft:
              element.marginLeft !== undefined
                ? `${element.marginLeft}px`
                : "0px",
            marginRight:
              element.marginRight !== undefined
                ? `${element.marginRight}px`
                : "0px",
            paddingTop:
              element.innerPaddingTop !== undefined
                ? `${element.innerPaddingTop}px`
                : "0px",
            paddingBottom:
              element.innerPaddingBottom !== undefined
                ? `${element.innerPaddingBottom}px`
                : "0px",
            paddingLeft:
              element.innerPaddingLeft !== undefined
                ? `${element.innerPaddingLeft}px`
                : "0px",
            paddingRight:
              element.innerPaddingRight !== undefined
                ? `${element.innerPaddingRight}px`
                : "0px",
          }}
        >
          {renderTextContent(element.text)}
        </Text>
      );
    }
    if (element.type === "image") {
      return (
        <div style={{ width: "100%", textAlign: element.align || "center" }}>
          <Img
            src={
              element.src ||
              "https://via.placeholder.com/300x200/2a0a4a/ffffff?text=Image"
            }
            width="100%"
            alt="Content"
            style={{
              ...styles.image,
              borderRadius: element.isHero && !isColumn ? "0" : "8px",
            }}
          />
        </div>
      );
    }
    if (element.type === "button") {
      const gradStart = element.gradientStart || "#a855f7";
      const gradEnd = element.gradientEnd || "#4f46e5";

      const btnBackground = element.useGradient
        ? `linear-gradient(to right, ${gradStart}, ${gradEnd})`
        : element.bgColor || "#a855f7";

      return (
        <div style={{ width: "100%", textAlign: element.align || "center" }}>
          <Button
            style={{
              ...styles.button,
              background: btnBackground,
              backgroundColor: element.bgColor || "#a855f7",
              color: element.color || "#ffffff",
              fontSize: element.fontSize ? `${element.fontSize}px` : "16px",
              fontWeight: element.isBold !== false ? "bold" : "normal",
              fontStyle: element.isItalic ? "italic" : "normal",
              textDecoration: element.isUnderline ? "underline" : "none",
              marginTop:
                element.marginTop !== undefined
                  ? `${element.marginTop}px`
                  : "0px",
              marginBottom:
                element.marginBottom !== undefined
                  ? `${element.marginBottom}px`
                  : "0px",
              marginLeft:
                element.marginLeft !== undefined
                  ? `${element.marginLeft}px`
                  : "0px",
              marginRight:
                element.marginRight !== undefined
                  ? `${element.marginRight}px`
                  : "0px",
            }}
            href={element.url || "https://legacyx.uk"}
          >
            {element.text}
          </Button>
        </div>
      );
    }
    if (element.type === "social") {
      return (
        <div
          style={{
            width: "100%",
            textAlign: element.align || "center",
            marginTop:
              element.marginTop !== undefined
                ? `${element.marginTop}px`
                : "0px",
            marginBottom:
              element.marginBottom !== undefined
                ? `${element.marginBottom}px`
                : "0px",
            marginLeft:
              element.marginLeft !== undefined
                ? `${element.marginLeft}px`
                : "0px",
            marginRight:
              element.marginRight !== undefined
                ? `${element.marginRight}px`
                : "0px",
            paddingTop:
              element.innerPaddingTop !== undefined
                ? `${element.innerPaddingTop}px`
                : "0px",
            paddingBottom:
              element.innerPaddingBottom !== undefined
                ? `${element.innerPaddingBottom}px`
                : "0px",
            paddingLeft:
              element.innerPaddingLeft !== undefined
                ? `${element.innerPaddingLeft}px`
                : "0px",
            paddingRight:
              element.innerPaddingRight !== undefined
                ? `${element.innerPaddingRight}px`
                : "0px",
          }}
        >
          {element.socials &&
            element.socials.map((social) => (
              <a
                key={social.id}
                href={social.url || "#"}
                style={{
                  display: "inline-block",
                  margin: "0 8px",
                  textDecoration: "none",
                }}
              >
                <Img
                  src={social.iconUrl}
                  width={element.iconSize || 24}
                  height={element.iconSize || 24}
                  alt={social.platform}
                  style={{ display: "block" }}
                />
              </a>
            ))}
        </div>
      );
    }
    return null;
  };

  return (
    <Html>
      <Head />
      <Preview>{globalSettings.previewText || "Update"}</Preview>
      <Body style={main}>
        <Container style={container}>
          {blocks.map((block) => {
            if (block.type === "divider") {
              return (
                <Section key={block.id} style={getSectionStyle(block)}>
                  <Hr
                    style={{
                      ...styles.divider,
                      marginTop:
                        block.marginTop !== undefined
                          ? `${block.marginTop}px`
                          : "0px",
                      marginBottom:
                        block.marginBottom !== undefined
                          ? `${block.marginBottom}px`
                          : "0px",
                    }}
                  />
                </Section>
              );
            }

            if (block.type === "split") {
              return (
                <Section key={block.id} style={getSectionStyle(block)}>
                  <Row>
                    <Column
                      style={{
                        width: "50%",
                        paddingRight: "8px",
                        verticalAlign: "top",
                      }}
                    >
                      {renderInnerElement(block.left, true)}
                    </Column>
                    <Column
                      style={{
                        width: "50%",
                        paddingLeft: "8px",
                        verticalAlign: "top",
                      }}
                    >
                      {renderInnerElement(block.right, true)}
                    </Column>
                  </Row>
                </Section>
              );
            }

            return (
              <Section key={block.id} style={getSectionStyle(block)}>
                {renderInnerElement(block)}
              </Section>
            );
          })}
        </Container>
      </Body>
    </Html>
  );
};

const SOCIAL_PLATFORMS = [
  {
    value: "facebook",
    label: "Facebook",
    defaultIcon: "https://img.icons8.com/ios-filled/50/ffffff/facebook-new.png",
  },
  {
    value: "instagram",
    label: "Instagram",
    defaultIcon:
      "https://img.icons8.com/ios-filled/50/ffffff/instagram-new.png",
  },
  {
    value: "x",
    label: "X (Twitter)",
    defaultIcon: "https://img.icons8.com/ios-filled/50/ffffff/twitterx--v1.png",
  },
  {
    value: "linkedin",
    label: "LinkedIn",
    defaultIcon: "https://img.icons8.com/ios-filled/50/ffffff/linkedin.png",
  },
  {
    value: "youtube",
    label: "YouTube",
    defaultIcon: "https://img.icons8.com/ios-filled/50/ffffff/youtube-play.png",
  },
  {
    value: "tiktok",
    label: "TikTok",
    defaultIcon: "https://img.icons8.com/ios-filled/50/ffffff/tiktok.png",
  },
  {
    value: "custom",
    label: "Custom Icon",
    defaultIcon: "https://img.icons8.com/ios-filled/50/ffffff/link.png",
  },
];

const CampaignBuilder = ({ appName }) => {
  const [builderMode, setBuilderMode] = useState("advanced");
  const [users, setUsers] = useState([]);
  const [notificationTitle, setNotificationTitle] = useState("");
  const [notificationText, setNotificationText] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [selectFromMS, setselectFromMS] = useState([]);
  const [extraEmail, setExtraEmail] = useState([]);
  const [liveHtmlPreview, setLiveHtmlPreview] = useState("");
  const [uploadingBlockId, setUploadingBlockId] = useState(null);

  const [templateName, setTemplateName] = useState("");
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [loadedTemplateId, setLoadedTemplateId] = useState(null);

  const [globalSettings, setGlobalSettings] = useState({
    pageColor: "#000000",
    containerColor: "#0a0a0a",
    borderColor: "#1f1f1f",
    fontFamily: "'Inter', sans-serif",
    headingColor: "#ffffff",
    textColor: "#a3a3a3",
    containerWidth: 600,
    previewText: `${appName.toUpperCase()} Update`,
  });

  const [blocks, setBlocks] = useState([
    {
      id: 1,
      type: "image",
      src: "https://via.placeholder.com/600x250/2a0a4a/ffffff?text=Header",
      isHero: true,
      align: "center",
      paddingTop: 0,
      paddingBottom: 0,
    },
    {
      id: 2,
      type: "heading",
      text: `Step into ${appName.toUpperCase()}`,
      align: "left",
      color: "#ffffff",
      paddingTop: 32,
      paddingBottom: 8,
      fontSize: 24,
      isBold: true,
      marginTop: 0,
      marginBottom: 0,
      marginLeft: 0,
      marginRight: 0,
    },
    {
      id: 3,
      type: "text",
      text: "Designed for modern connection.",
      align: "left",
      color: "#a3a3a3",
      paddingTop: 0,
      paddingBottom: 16,
      fontSize: 15,
      marginTop: 0,
      marginBottom: 0,
      marginLeft: 0,
      marginRight: 0,
    },
    {
      id: 4,
      type: "divider",
      paddingTop: 16,
      paddingBottom: 16,
      marginTop: 0,
      marginBottom: 0,
    },
    {
      id: 5,
      type: "button",
      text: "Open App",
      url: "https://legacyx.uk",
      align: "center",
      bgColor: "#a855f7",
      color: "#ffffff",
      paddingTop: 16,
      paddingBottom: 32,
      fontSize: 16,
      isBold: true,
      useGradient: true,
      gradientStart: "#a855f7",
      gradientEnd: "#4f46e5",
      marginTop: 0,
      marginBottom: 0,
      marginLeft: 0,
      marginRight: 0,
    },
  ]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        if (appName === "lxdc") {
          const response = await fetch(base_url + "/auth/allProfile");
          if (!response.ok) throw new Error("Failed to fetch LXDC users");

          const data = await response.json();
          let usersWithRequiredFields = data?.users
            ?.filter((user) => !!user.username?.trim())
            ?.map((user) => ({
              value: user._id,
              label: user.username,
              email: user.email,
            }));

          setUsers(usersWithRequiredFields || []);
        } else if (appName === "vortex") {
          await loginAdmin();
          const usersCollection = collection(db, "users");
          const userSnapshot = await getDocs(usersCollection);
          const usersList = userSnapshot.docs.map((doc) => ({
            id: doc.id,
            uid: doc.data().uid || doc.id,
            ...doc.data(),
          }));

          let usersWithRequiredFields = usersList
            ?.filter((user) => !!user.name?.trim())
            ?.map((user) => ({
              value: user.id || user.uid,
              label: user.name,
              email: user.email,
            }));

          setUsers(usersWithRequiredFields || []);
        }
      } catch (error) {
        console.error(error);
      }
    };

    const fetchTemplates = async () => {
      try {
        const templatesCol = collection(db, "emailTemplates");
        const snapshot = await getDocs(templatesCol);
        const tplList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSavedTemplates(tplList);
      } catch (error) {
        console.error(error);
      }
    };

    fetchUsers();
    fetchTemplates();
  }, [appName]);

  useEffect(() => {
    if (builderMode === "advanced") {
      const compileEmail = async () => {
        try {
          const htmlString = await render(
            <LegacyXTemplate blocks={blocks} globalSettings={globalSettings} />,
            {
              pretty: true,
            },
          );
          setLiveHtmlPreview(htmlString);
        } catch (error) {
          console.error(error);
        }
      };
      compileEmail();
    }
  }, [blocks, globalSettings, builderMode]);

  const handleNotificationTitle = (event) => {
    const capitalizedValue =
      event.target.value.charAt(0).toUpperCase() + event.target.value.slice(1);
    setNotificationTitle(capitalizedValue);
  };

  const handleNotificationTextChange = (event) => {
    const capitalizedValue =
      event.target.value.charAt(0).toUpperCase() + event.target.value.slice(1);
    setNotificationText(capitalizedValue);
  };

  const removeEmail = (i) => {
    setExtraEmail(
      extraEmail.filter((data, index) => {
        return index !== i;
      }),
    );
  };

  const AddEmail = (data) => {
    setExtraEmail([...extraEmail, { email: data?.Enteremail }]);
  };

  const cleanBlocksForStorage = (currentBlocks) => {
    return currentBlocks.map((block) => {
      let cleanBlock = JSON.parse(JSON.stringify(block));

      if (
        cleanBlock.type === "image" &&
        cleanBlock.src?.startsWith("data:image")
      ) {
        cleanBlock.src = "";
      }

      if (
        cleanBlock.useBgImage &&
        cleanBlock.bgImage?.startsWith("data:image")
      ) {
        cleanBlock.bgImage = "";
      }

      if (cleanBlock.type === "split") {
        if (
          cleanBlock.left.type === "image" &&
          cleanBlock.left.src?.startsWith("data:image")
        ) {
          cleanBlock.left.src = "";
        }
        if (
          cleanBlock.right.type === "image" &&
          cleanBlock.right.src?.startsWith("data:image")
        ) {
          cleanBlock.right.src = "";
        }
        if (cleanBlock.left.bgImage?.startsWith("data:image")) {
          cleanBlock.left.bgImage = "";
        }
        if (cleanBlock.right.bgImage?.startsWith("data:image")) {
          cleanBlock.right.bgImage = "";
        }
      }

      return cleanBlock;
    });
  };

  const saveCurrentTemplate = async () => {
    if (!templateName.trim()) {
      toast.error("Template name cannot be empty.");
      return;
    }
    try {
      const sanitizedBlocks = cleanBlocksForStorage(blocks);

      const newTemplate = {
        name: templateName,
        blocks: JSON.stringify(sanitizedBlocks),
        globalSettings: JSON.stringify(globalSettings),
        createdAt: new Date().toISOString(),
      };

      const docRef = await addDoc(
        collection(db, "emailTemplates"),
        newTemplate,
      );
      setSavedTemplates([...savedTemplates, { id: docRef.id, ...newTemplate }]);
      setLoadedTemplateId(docRef.id);
      toast.success("Template architecture saved.");
    } catch (error) {
      console.error(error);
      toast.error("Failed to commit template to database.");
    }
  };

  const updateCurrentTemplate = async () => {
    if (!loadedTemplateId) return;
    if (!templateName.trim()) {
      toast.error("Template name cannot be empty.");
      return;
    }
    try {
      const sanitizedBlocks = cleanBlocksForStorage(blocks);
      const templateRef = doc(db, "emailTemplates", loadedTemplateId);

      const updatedData = {
        name: templateName,
        blocks: JSON.stringify(sanitizedBlocks),
        globalSettings: JSON.stringify(globalSettings),
        updatedAt: new Date().toISOString(),
      };

      await updateDoc(templateRef, updatedData);
      setSavedTemplates(
        savedTemplates.map((t) =>
          t.id === loadedTemplateId ? { ...t, ...updatedData } : t,
        ),
      );
      toast.success("Template successfully updated.");
    } catch (error) {
      console.error(error);
      toast.error("Failed to update template.");
    }
  };

  const loadTemplate = (templateId) => {
    if (!templateId) {
      setLoadedTemplateId(null);
      setTemplateName("");
      return;
    }
    const tpl = savedTemplates.find((t) => t.id === templateId);
    if (tpl) {
      const parsedBlocks =
        typeof tpl.blocks === "string" ? JSON.parse(tpl.blocks) : tpl.blocks;
      const parsedSettings =
        typeof tpl.globalSettings === "string"
          ? JSON.parse(tpl.globalSettings)
          : tpl.globalSettings;

      setBlocks(parsedBlocks);
      setGlobalSettings(parsedSettings);
      setTemplateName(tpl.name);
      setLoadedTemplateId(tpl.id);
      toast.success(`${tpl.name} loaded into the builder.`);
    }
  };

  const handleImageUpload = (e, blockId, side = null, isBackground = false) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Embedded image payload must be less than 2MB.");
      return;
    }

    const uploadTrackerId = `${blockId}-${side || "main"}-${isBackground ? "bg" : "src"}`;
    setUploadingBlockId(uploadTrackerId);

    const reader = new FileReader();

    reader.onloadend = () => {
      const base64String = reader.result;
      const targetKey = isBackground ? "bgImage" : "src";

      if (side) {
        updateColumnContent(blockId, side, targetKey, base64String);
      } else {
        updateBlock(blockId, targetKey, base64String);
      }

      toast.success("Image asset embedded directly into payload.");
      setUploadingBlockId(null);
      e.target.value = null;
    };

    reader.onerror = () => {
      toast.error("Failed to encode image data.");
      setUploadingBlockId(null);
      e.target.value = null;
    };

    reader.readAsDataURL(file);
  };

  const updateGlobalSetting = (key, value) => {
    setGlobalSettings({ ...globalSettings, [key]: value });
  };

  const addBlock = (type) => {
    if (type === "button") {
      const buttonCount = blocks.filter(
        (b) =>
          b.type === "button" ||
          (b.type === "split" &&
            (b.left.type === "button" || b.right.type === "button")),
      ).length;
      if (buttonCount >= 5) {
        toast.error("You can only add up to 5 buttons per email.");
        return;
      }
    }

    const newBlock = {
      id: Date.now(),
      type,
      align: "left",
      paddingTop: 16,
      paddingBottom: 16,
      useBgImage: false,
      bgImage: "",
      fallbackBgColor: "transparent",
    };

    if (type === "heading") {
      newBlock.text = "New Heading";
      newBlock.color = globalSettings.headingColor;
      newBlock.fontSize = 24;
      newBlock.isBold = true;
      newBlock.marginTop = 0;
      newBlock.marginBottom = 0;
      newBlock.marginLeft = 0;
      newBlock.marginRight = 0;
      newBlock.innerPaddingTop = 0;
      newBlock.innerPaddingBottom = 0;
      newBlock.innerPaddingLeft = 0;
      newBlock.innerPaddingRight = 0;
    }
    if (type === "text") {
      newBlock.text = "Enter your text here...";
      newBlock.color = globalSettings.textColor;
      newBlock.fontSize = 15;
      newBlock.marginTop = 0;
      newBlock.marginBottom = 0;
      newBlock.marginLeft = 0;
      newBlock.marginRight = 0;
      newBlock.innerPaddingTop = 0;
      newBlock.innerPaddingBottom = 0;
      newBlock.innerPaddingLeft = 0;
      newBlock.innerPaddingRight = 0;
    }
    if (type === "image") {
      newBlock.src = "";
      newBlock.isHero = false;
      newBlock.align = "center";
      newBlock.paddingTop = 0;
      newBlock.paddingBottom = 0;
    }
    if (type === "button") {
      newBlock.text = "Click Here";
      newBlock.url = "https://";
      newBlock.align = "center";
      newBlock.bgColor = "#a855f7";
      newBlock.color = "#ffffff";
      newBlock.fontSize = 16;
      newBlock.isBold = true;
      newBlock.useGradient = false;
      newBlock.gradientStart = "#a855f7";
      newBlock.gradientEnd = "#4f46e5";
      newBlock.marginTop = 0;
      newBlock.marginBottom = 0;
      newBlock.marginLeft = 0;
      newBlock.marginRight = 0;
    }
    if (type === "social") {
      newBlock.socials = [];
      newBlock.iconSize = 24;
      newBlock.align = "center";
      newBlock.marginTop = 0;
      newBlock.marginBottom = 0;
      newBlock.marginLeft = 0;
      newBlock.marginRight = 0;
      newBlock.innerPaddingTop = 0;
      newBlock.innerPaddingBottom = 0;
      newBlock.innerPaddingLeft = 0;
      newBlock.innerPaddingRight = 0;
    }
    if (type === "divider") {
      newBlock.marginTop = 0;
      newBlock.marginBottom = 0;
    }
    if (type === "split") {
      newBlock.left = {
        type: "text",
        text: "Left column text",
        align: "left",
        color: globalSettings.textColor,
        fontSize: 15,
        marginTop: 0,
        marginBottom: 0,
        marginLeft: 0,
        marginRight: 0,
      };
      newBlock.right = {
        type: "text",
        text: "Right column text",
        align: "left",
        color: globalSettings.textColor,
        fontSize: 15,
        marginTop: 0,
        marginBottom: 0,
        marginLeft: 0,
        marginRight: 0,
      };
    }

    setBlocks([...blocks, newBlock]);
  };

  const updateBlock = (id, key, value) => {
    setBlocks(
      blocks.map((block) =>
        block.id === id ? { ...block, [key]: value } : block,
      ),
    );
  };

  const updateColumnContent = (blockId, side, key, value) => {
    setBlocks(
      blocks.map((block) => {
        if (block.id === blockId) {
          return { ...block, [side]: { ...block[side], [key]: value } };
        }
        return block;
      }),
    );
  };

  const changeColumnType = (blockId, side, newType) => {
    setBlocks(
      blocks.map((block) => {
        if (block.id === blockId) {
          const newContent = {
            type: newType,
            align:
              newType === "image" ||
              newType === "button" ||
              newType === "social"
                ? "center"
                : "left",
          };
          if (newType === "heading") {
            newContent.text = "Heading";
            newContent.color = globalSettings.headingColor;
            newContent.fontSize = 24;
            newContent.isBold = true;
            newContent.marginTop = 0;
            newContent.marginBottom = 0;
            newContent.marginLeft = 0;
            newContent.marginRight = 0;
            newContent.innerPaddingTop = 0;
            newContent.innerPaddingBottom = 0;
            newContent.innerPaddingLeft = 0;
            newContent.innerPaddingRight = 0;
          }
          if (newType === "text") {
            newContent.text = "Text content...";
            newContent.color = globalSettings.textColor;
            newContent.fontSize = 15;
            newContent.marginTop = 0;
            newContent.marginBottom = 0;
            newContent.marginLeft = 0;
            newContent.marginRight = 0;
            newContent.innerPaddingTop = 0;
            newContent.innerPaddingBottom = 0;
            newContent.innerPaddingLeft = 0;
            newContent.innerPaddingRight = 0;
          }
          if (newType === "image") {
            newContent.src = "";
            newContent.isHero = false;
          }
          if (newType === "social") {
            newContent.socials = [];
            newContent.iconSize = 24;
            newContent.marginTop = 0;
            newContent.marginBottom = 0;
            newContent.marginLeft = 0;
            newContent.marginRight = 0;
            newContent.innerPaddingTop = 0;
            newContent.innerPaddingBottom = 0;
            newContent.innerPaddingLeft = 0;
            newContent.innerPaddingRight = 0;
          }
          if (newType === "button") {
            newContent.text = "Click Here";
            newContent.url = "https://";
            newContent.bgColor = "#a855f7";
            newContent.color = "#ffffff";
            newContent.fontSize = 16;
            newContent.isBold = true;
            newContent.useGradient = false;
            newContent.gradientStart = "#a855f7";
            newContent.gradientEnd = "#4f46e5";
            newContent.marginTop = 0;
            newContent.marginBottom = 0;
            newContent.marginLeft = 0;
            newContent.marginRight = 0;
          }
          return { ...block, [side]: newContent };
        }
        return block;
      }),
    );
  };

  const removeBlock = (id) => {
    setBlocks(blocks.filter((block) => block.id !== id));
  };

  const moveBlock = (index, direction) => {
    const newBlocks = [...blocks];
    if (direction === "up" && index > 0) {
      [newBlocks[index - 1], newBlocks[index]] = [
        newBlocks[index],
        newBlocks[index - 1],
      ];
    } else if (direction === "down" && index < newBlocks.length - 1) {
      [newBlocks[index + 1], newBlocks[index]] = [
        newBlocks[index],
        newBlocks[index + 1],
      ];
    }
    setBlocks(newBlocks);
  };

  const sendEmail = (event) => {
    event.preventDefault();

    if (notificationTitle.length === 0) {
      toast.error("Subject Title cannot be empty");
      return;
    }

    if (builderMode === "basic" && notificationText.length === 0) {
      toast.error("Email body cannot be empty in Basic Mode.");
      return;
    }

    if (
      builderMode === "advanced" &&
      (!liveHtmlPreview ||
        typeof liveHtmlPreview !== "string" ||
        liveHtmlPreview.trim() === "")
    ) {
      toast.error("Email body failed to render in Advanced Mode.");
      return;
    }

    let selectedUsers = users
      .filter((user) => selectFromMS.some((item) => item.value === user.value))
      .map((user) => ({
        id: user.value,
        email: user.email,
      }));

    selectedUsers = [...selectedUsers, ...extraEmail];

    if (selectedUsers.length === 0) {
      toast.error("Please select at least one recipient");
      return;
    }

    setLoading(true);

    const payload = {
      subject: notificationTitle,
      to: selectedUsers,
      app: appName,
    };

    if (builderMode === "basic") {
      payload.body = notificationText;
    } else {
      payload.htmlBody = liveHtmlPreview;
    }

    fetch(base_url + "/auth/sendDirectEmail", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (response.ok) {
          toast.success("Email sent successfully");
          setNotificationTitle("");
          if (builderMode === "basic") setNotificationText("");
        } else {
          toast.error("Failed to send Email");
          throw new Error("failed to send email");
        }
        return response.json();
      })
      .then((data) => {
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  const renderBackgroundControls = (blockId, blockData) => {
    if (blockData.type === "image") return null;

    return (
      <div className="flex flex-col gap-3 p-3 bg-slate-100 rounded border border-slate-200 mt-3">
        <label className="flex items-center gap-2 text-xs font-bold text-slate-600 cursor-pointer">
          <input
            type="checkbox"
            checked={blockData.useBgImage || false}
            onChange={(e) =>
              updateBlock(blockId, "useBgImage", e.target.checked)
            }
            className="rounded text-indigo-600 focus:ring-indigo-500"
          />
          Overlay Element on Background Image
        </label>

        {blockData.useBgImage && (
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <input
                type="text"
                value={blockData.bgImage || ""}
                onChange={(e) =>
                  updateBlock(blockId, "bgImage", e.target.value)
                }
                placeholder="Background URL..."
                className="flex-1 w-full outline-none px-3 py-1.5 text-xs rounded border border-slate-300 focus:border-indigo-500"
              />
              <label
                className={`flex items-center justify-center px-3 py-1.5 rounded border border-slate-300 text-[10px] font-bold uppercase cursor-pointer ${uploadingBlockId === `${blockId}-main-bg` ? "bg-slate-200 text-slate-400" : "bg-white text-indigo-700 hover:bg-slate-50"}`}
              >
                {uploadingBlockId === `${blockId}-main-bg` ? "..." : "Upload"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={uploadingBlockId === `${blockId}-main-bg`}
                  onChange={(e) => handleImageUpload(e, blockId, null, true)}
                />
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={blockData.fallbackBgColor || "transparent"}
                onChange={(e) =>
                  updateBlock(blockId, "fallbackBgColor", e.target.value)
                }
                className="w-5 h-5 border-0 p-0 rounded cursor-pointer"
              />
              <span className="text-[10px] text-slate-500 uppercase font-bold">
                Fallback Solid Color
              </span>
            </div>
          </div>
        )}
        {!blockData.useBgImage && (
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={blockData.fallbackBgColor || "transparent"}
              onChange={(e) =>
                updateBlock(blockId, "fallbackBgColor", e.target.value)
              }
              className="w-5 h-5 border-0 p-0 rounded cursor-pointer"
            />
            <span className="text-[10px] text-slate-500 uppercase font-bold">
              Container Background Color
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderSpacingControls = (content, onChangeHandler) => {
    return (
      <div className="flex flex-col gap-2 mt-2 p-2 bg-slate-50 border border-slate-200 rounded">
        <div className="text-[10px] font-bold text-slate-500 uppercase">
          Margin Matrix (Px)
        </div>
        <div className="grid grid-cols-4 gap-2">
          <div className="flex flex-col">
            <span className="text-[9px] text-slate-400 text-center mb-0.5">
              TOP
            </span>
            <input
              type="number"
              value={content.marginTop || 0}
              onChange={(e) =>
                onChangeHandler("marginTop", parseInt(e.target.value) || 0)
              }
              className="w-full outline-none px-1 py-1 text-xs text-center rounded border border-slate-300 focus:border-indigo-500"
            />
          </div>
          <div className="flex flex-col">
            <span className="text-[9px] text-slate-400 text-center mb-0.5">
              RIGHT
            </span>
            <input
              type="number"
              value={content.marginRight || 0}
              onChange={(e) =>
                onChangeHandler("marginRight", parseInt(e.target.value) || 0)
              }
              className="w-full outline-none px-1 py-1 text-xs text-center rounded border border-slate-300 focus:border-indigo-500"
            />
          </div>
          <div className="flex flex-col">
            <span className="text-[9px] text-slate-400 text-center mb-0.5">
              BOTTOM
            </span>
            <input
              type="number"
              value={content.marginBottom || 0}
              onChange={(e) =>
                onChangeHandler("marginBottom", parseInt(e.target.value) || 0)
              }
              className="w-full outline-none px-1 py-1 text-xs text-center rounded border border-slate-300 focus:border-indigo-500"
            />
          </div>
          <div className="flex flex-col">
            <span className="text-[9px] text-slate-400 text-center mb-0.5">
              LEFT
            </span>
            <input
              type="number"
              value={content.marginLeft || 0}
              onChange={(e) =>
                onChangeHandler("marginLeft", parseInt(e.target.value) || 0)
              }
              className="w-full outline-none px-1 py-1 text-xs text-center rounded border border-slate-300 focus:border-indigo-500"
            />
          </div>
        </div>
        <div className="text-[10px] font-bold text-slate-500 uppercase mt-2">
          Padding Matrix (Px)
        </div>
        <div className="grid grid-cols-4 gap-2">
          <div className="flex flex-col">
            <span className="text-[9px] text-slate-400 text-center mb-0.5">
              TOP
            </span>
            <input
              type="number"
              value={content.innerPaddingTop || 0}
              onChange={(e) =>
                onChangeHandler(
                  "innerPaddingTop",
                  parseInt(e.target.value) || 0,
                )
              }
              className="w-full outline-none px-1 py-1 text-xs text-center rounded border border-slate-300 focus:border-indigo-500"
            />
          </div>
          <div className="flex flex-col">
            <span className="text-[9px] text-slate-400 text-center mb-0.5">
              RIGHT
            </span>
            <input
              type="number"
              value={content.innerPaddingRight || 0}
              onChange={(e) =>
                onChangeHandler(
                  "innerPaddingRight",
                  parseInt(e.target.value) || 0,
                )
              }
              className="w-full outline-none px-1 py-1 text-xs text-center rounded border border-slate-300 focus:border-indigo-500"
            />
          </div>
          <div className="flex flex-col">
            <span className="text-[9px] text-slate-400 text-center mb-0.5">
              BOTTOM
            </span>
            <input
              type="number"
              value={content.innerPaddingBottom || 0}
              onChange={(e) =>
                onChangeHandler(
                  "innerPaddingBottom",
                  parseInt(e.target.value) || 0,
                )
              }
              className="w-full outline-none px-1 py-1 text-xs text-center rounded border border-slate-300 focus:border-indigo-500"
            />
          </div>
          <div className="flex flex-col">
            <span className="text-[9px] text-slate-400 text-center mb-0.5">
              LEFT
            </span>
            <input
              type="number"
              value={content.innerPaddingLeft || 0}
              onChange={(e) =>
                onChangeHandler(
                  "innerPaddingLeft",
                  parseInt(e.target.value) || 0,
                )
              }
              className="w-full outline-none px-1 py-1 text-xs text-center rounded border border-slate-300 focus:border-indigo-500"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderControlInputs = (
    content,
    onChangeHandler,
    blockId,
    side = null,
  ) => {
    if (content.type === "empty")
      return (
        <div className="text-sm text-slate-400 italic py-4 text-center">
          Empty Column
        </div>
      );

    return (
      <div className="flex flex-col gap-2 mt-2">
        {(content.type === "heading" || content.type === "text") && (
          <>
            <textarea
              rows={content.type === "heading" ? "1" : "3"}
              value={content.text}
              onChange={(e) => onChangeHandler("text", e.target.value)}
              className="w-full outline-none px-3 py-2 text-sm rounded-md text-slate-900 bg-white border border-slate-200 focus:border-indigo-500 transition-all resize-none"
            />
            <div className="flex justify-between items-center gap-2">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={content.color}
                  onChange={(e) => onChangeHandler("color", e.target.value)}
                  className="w-6 h-6 border-0 p-0 rounded cursor-pointer"
                />
                <span className="text-[10px] text-slate-500 uppercase font-bold">
                  Color
                </span>
              </div>
              <div className="flex items-center gap-1 border border-slate-200 rounded p-0.5 bg-white">
                <button
                  onClick={() => onChangeHandler("isBold", !content.isBold)}
                  className={`w-6 h-6 rounded text-xs font-bold ${content.isBold ? "bg-indigo-100 text-indigo-700" : "text-slate-600 hover:bg-slate-100"}`}
                >
                  B
                </button>
                <button
                  onClick={() => onChangeHandler("isItalic", !content.isItalic)}
                  className={`w-6 h-6 rounded text-xs italic ${content.isItalic ? "bg-indigo-100 text-indigo-700" : "text-slate-600 hover:bg-slate-100"}`}
                >
                  I
                </button>
                <button
                  onClick={() =>
                    onChangeHandler("isUnderline", !content.isUnderline)
                  }
                  className={`w-6 h-6 rounded text-xs underline ${content.isUnderline ? "bg-indigo-100 text-indigo-700" : "text-slate-600 hover:bg-slate-100"}`}
                >
                  U
                </button>
              </div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  value={
                    content.fontSize || (content.type === "heading" ? 24 : 15)
                  }
                  onChange={(e) =>
                    onChangeHandler("fontSize", parseInt(e.target.value) || 0)
                  }
                  className="w-12 outline-none px-1 py-1 text-xs rounded border border-slate-200"
                />
                <span className="text-[10px] text-slate-500 font-bold">PX</span>
              </div>
            </div>
            {renderSpacingControls(content, onChangeHandler)}
          </>
        )}
        {content.type === "social" && (
          <div className="flex flex-col gap-3 mt-2 p-2 bg-slate-50 rounded border border-slate-200">
            <div className="flex items-center gap-2">
              <select
                value=""
                onChange={(e) => {
                  if (!e.target.value) return;
                  const platform = SOCIAL_PLATFORMS.find(
                    (p) => p.value === e.target.value,
                  );
                  const newSocials = [
                    ...(content.socials || []),
                    {
                      id: Date.now(),
                      platform: platform.value,
                      url: "",
                      iconUrl: platform.defaultIcon,
                    },
                  ];
                  onChangeHandler("socials", newSocials);
                }}
                className="w-full outline-none px-3 py-2 text-sm rounded border border-slate-300 focus:border-indigo-500"
              >
                <option value="">+ Select Platform to Add...</option>
                {SOCIAL_PLATFORMS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>

            {content.socials && content.socials.length > 0 && (
              <div className="flex flex-col gap-2">
                {content.socials.map((social, sIndex) => (
                  <div
                    key={social.id}
                    className="flex flex-wrap items-center gap-2 bg-white p-2 rounded border border-slate-200"
                  >
                    <img
                      src={social.iconUrl}
                      alt="icon"
                      className="w-6 h-6 bg-slate-800 rounded p-1 flex-shrink-0"
                    />
                    <input
                      type="text"
                      value={social.url}
                      onChange={(e) => {
                        const newSocials = [...content.socials];
                        newSocials[sIndex].url = e.target.value;
                        onChangeHandler("socials", newSocials);
                      }}
                      placeholder="Platform URL"
                      className="flex-1 min-w-[80px] outline-none px-2 py-1 text-xs rounded border border-slate-300 focus:border-indigo-500"
                    />
                    <input
                      type="text"
                      value={social.iconUrl}
                      onChange={(e) => {
                        const newSocials = [...content.socials];
                        newSocials[sIndex].iconUrl = e.target.value;
                        onChangeHandler("socials", newSocials);
                      }}
                      placeholder="Icon URL"
                      className="flex-1 min-w-[80px] outline-none px-2 py-1 text-xs rounded border border-slate-300 focus:border-indigo-500"
                    />
                    <button
                      onClick={() => {
                        const newSocials = content.socials.filter(
                          (s) => s.id !== social.id,
                        );
                        onChangeHandler("socials", newSocials);
                      }}
                      className="p-1 text-red-500 hover:bg-red-50 rounded flex-shrink-0"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        ></path>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2 mt-2 pt-3 border-t border-slate-200">
              <span className="text-[10px] text-slate-500 font-bold uppercase">
                Icon Size (PX)
              </span>
              <input
                type="number"
                value={content.iconSize || 24}
                onChange={(e) =>
                  onChangeHandler("iconSize", parseInt(e.target.value) || 0)
                }
                className="w-16 outline-none px-2 py-1.5 text-sm rounded border border-slate-300 focus:border-indigo-500"
              />
            </div>
            {renderSpacingControls(content, onChangeHandler)}
          </div>
        )}
        {content.type === "image" && (
          <div className="flex gap-2">
            <input
              type="text"
              value={content.src}
              placeholder="Image URL..."
              onChange={(e) => onChangeHandler("src", e.target.value)}
              className="flex-1 w-full outline-none px-3 py-2 text-sm rounded-md text-slate-900 bg-white border border-slate-200 focus:border-indigo-500 transition-all"
            />
            <label
              className={`flex items-center justify-center px-3 py-2 rounded-md border border-slate-200 text-xs font-bold uppercase cursor-pointer transition-all ${uploadingBlockId === `${blockId}-${side || "main"}-src` ? "bg-slate-100 text-slate-400" : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border-indigo-200"}`}
            >
              {uploadingBlockId === `${blockId}-${side || "main"}-src`
                ? "..."
                : "Embed"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                disabled={
                  uploadingBlockId === `${blockId}-${side || "main"}-src`
                }
                onChange={(e) => handleImageUpload(e, blockId, side)}
              />
            </label>
          </div>
        )}
        {content.type === "button" && (
          <>
            <input
              type="text"
              value={content.text}
              placeholder="Button Text"
              onChange={(e) => onChangeHandler("text", e.target.value)}
              className="w-full outline-none px-3 py-2 text-sm rounded-md text-slate-900 bg-white border border-slate-200 focus:border-indigo-500 transition-all"
            />
            <input
              type="text"
              value={content.url}
              placeholder="URL"
              onChange={(e) => onChangeHandler("url", e.target.value)}
              className="w-full outline-none px-3 py-2 text-sm rounded-md text-slate-900 bg-white border border-slate-200 focus:border-indigo-500 transition-all"
            />

            <div className="flex flex-col gap-2 p-2 bg-slate-100 rounded border border-slate-200">
              <label className="flex items-center gap-2 text-xs font-bold text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={content.useGradient || false}
                  onChange={(e) =>
                    onChangeHandler("useGradient", e.target.checked)
                  }
                  className="rounded text-indigo-600 focus:ring-indigo-500"
                />
                Use Gradient Background
              </label>

              {content.useGradient ? (
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-1">
                    <input
                      type="color"
                      value={content.gradientStart || "#a855f7"}
                      onChange={(e) =>
                        onChangeHandler("gradientStart", e.target.value)
                      }
                      className="w-5 h-5 border-0 p-0 rounded cursor-pointer"
                    />
                    <span className="text-[10px] text-slate-500 uppercase font-bold">
                      Start
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <input
                      type="color"
                      value={content.gradientEnd || "#4f46e5"}
                      onChange={(e) =>
                        onChangeHandler("gradientEnd", e.target.value)
                      }
                      className="w-5 h-5 border-0 p-0 rounded cursor-pointer"
                    />
                    <span className="text-[10px] text-slate-500 uppercase font-bold">
                      End
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={content.bgColor}
                    onChange={(e) => onChangeHandler("bgColor", e.target.value)}
                    className="w-6 h-6 border-0 p-0 rounded cursor-pointer"
                  />
                  <span className="text-xs text-slate-500 uppercase font-bold">
                    Solid Background
                  </span>
                </div>
              )}

              <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={content.color}
                    onChange={(e) => onChangeHandler("color", e.target.value)}
                    className="w-6 h-6 border-0 p-0 rounded cursor-pointer"
                  />
                  <span className="text-xs text-slate-500 uppercase font-bold">
                    Text
                  </span>
                </div>
                <div className="flex items-center gap-1 border border-slate-200 rounded p-0.5 bg-white">
                  <button
                    onClick={() => onChangeHandler("isBold", !content.isBold)}
                    className={`w-5 h-5 rounded text-[10px] font-bold ${content.isBold ? "bg-indigo-100 text-indigo-700" : "text-slate-600 hover:bg-slate-100"}`}
                  >
                    B
                  </button>
                  <button
                    onClick={() =>
                      onChangeHandler("isItalic", !content.isItalic)
                    }
                    className={`w-5 h-5 rounded text-[10px] italic ${content.isItalic ? "bg-indigo-100 text-indigo-700" : "text-slate-600 hover:bg-slate-100"}`}
                  >
                    I
                  </button>
                </div>
              </div>
            </div>
            {renderSpacingControls(content, onChangeHandler)}
          </>
        )}
        {content.type !== "divider" && (
          <div className="flex gap-1 mt-1">
            {["left", "center", "right"].map((align) => (
              <button
                key={align}
                onClick={() => onChangeHandler("align", align)}
                className={`px-2 py-1 text-[10px] uppercase font-bold rounded ${
                  content.align === align
                    ? "bg-indigo-100 text-indigo-700"
                    : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                }`}
              >
                {align}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <section className="flex-1 w-full ml-[19rem] mt-[8rem] bg-slate-50 flex flex-col items-start justify-start pt-[2.5rem] px-[3rem] pb-[10rem] box-border gap-[1rem] max-w-full text-left font-sans">
      <ToastContainer className="text-[1rem]" />

      <div className="w-full flex justify-between items-center mb-4">
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
          Campaign Builder
        </h1>
        <div className="flex items-center bg-white p-1 rounded-lg border border-slate-200 shadow-sm">
          <button
            onClick={() => setBuilderMode("basic")}
            className={`px-4 py-2 text-sm font-semibold rounded-md transition-all ${builderMode === "basic" ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          >
            Basic Mode
          </button>
          <button
            onClick={() => setBuilderMode("advanced")}
            className={`px-4 py-2 text-sm font-semibold rounded-md transition-all ${builderMode === "advanced" ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          >
            Advanced Mode
          </button>
        </div>
      </div>

      <div className="w-full bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
        <div className="flex flex-col gap-6">
          <div className="w-full flex flex-col gap-2">
            <label className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
              Subject Line (For The Inbox)
            </label>
            <input
              type="text"
              placeholder={`e.g., Welcome to ${appName}`}
              value={notificationTitle}
              onChange={handleNotificationTitle}
              className="w-full outline-none px-4 py-3 rounded-lg text-slate-900 bg-slate-50 border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
              required
            />
          </div>

          <div className="w-full flex flex-col gap-2">
            <label className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
              Select Recipients
            </label>
            <div className="w-full">
              <MultiSelect
                className="text-slate-900 custom-multi-select"
                valueRenderer={(selected, _options) => {
                  return selected.length
                    ? selected.map(({ label }) => label).join(", ")
                    : "No Users Selected";
                }}
                options={users}
                value={selectFromMS}
                onChange={setselectFromMS}
                labelledBy="Search and select users..."
              />
            </div>
            <MultipleEmailForm
              onDelete={removeEmail}
              Emails={extraEmail}
              onAdd={AddEmail}
            />
          </div>
        </div>
      </div>

      {builderMode === "basic" && (
        <div className="w-full bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <label className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-2 block">
            Email Text Content
          </label>
          <textarea
            className="w-full outline-none resize-none h-[12.375rem] rounded-lg bg-slate-50 border border-slate-300 p-4 font-sans text-[1rem] text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Write your basic notification here..."
            value={notificationText}
            onChange={handleNotificationTextChange}
            required
          />
        </div>
      )}

      {builderMode === "advanced" && (
        <>
          <div className="w-full bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-slate-800">
                Global Canvas Settings
              </h2>
              <div className="flex items-center gap-3">
                <select
                  onChange={(e) => loadTemplate(e.target.value)}
                  value={loadedTemplateId || ""}
                  className="outline-none px-3 py-2 text-sm rounded border border-slate-300 bg-slate-50 focus:border-indigo-500"
                >
                  <option value="">Load Saved Template...</option>
                  {savedTemplates.map((tpl) => (
                    <option key={tpl.id} value={tpl.id}>
                      {tpl.name}
                    </option>
                  ))}
                </select>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Template Name"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    className="outline-none px-3 py-2 text-sm rounded border border-slate-300 bg-slate-50 focus:border-indigo-500 w-40"
                  />
                  {loadedTemplateId && (
                    <button
                      onClick={updateCurrentTemplate}
                      className="bg-indigo-600 text-white px-4 py-2 rounded text-sm font-bold hover:bg-indigo-700"
                    >
                      Update
                    </button>
                  )}
                  <button
                    onClick={saveCurrentTemplate}
                    className="bg-indigo-50 text-indigo-700 border border-indigo-200 px-4 py-2 rounded text-sm font-bold hover:bg-indigo-100"
                  >
                    Save New
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-6 border-t border-slate-100 pt-4">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Page Background
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={globalSettings.pageColor}
                    onChange={(e) =>
                      updateGlobalSetting("pageColor", e.target.value)
                    }
                    className="w-10 h-10 border-0 p-0 rounded cursor-pointer"
                  />
                  <span className="text-sm font-mono text-slate-600">
                    {globalSettings.pageColor}
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Container Box
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={globalSettings.containerColor}
                    onChange={(e) =>
                      updateGlobalSetting("containerColor", e.target.value)
                    }
                    className="w-10 h-10 border-0 p-0 rounded cursor-pointer"
                  />
                  <span className="text-sm font-mono text-slate-600">
                    {globalSettings.containerColor}
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Borders & Dividers
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={globalSettings.borderColor}
                    onChange={(e) =>
                      updateGlobalSetting("borderColor", e.target.value)
                    }
                    className="w-10 h-10 border-0 p-0 rounded cursor-pointer"
                  />
                  <span className="text-sm font-mono text-slate-600">
                    {globalSettings.borderColor}
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Global Font Family
                </label>
                <select
                  value={globalSettings.fontFamily}
                  onChange={(e) =>
                    updateGlobalSetting("fontFamily", e.target.value)
                  }
                  className="w-full outline-none px-3 py-2 rounded-lg text-slate-900 bg-slate-50 border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                >
                  <option value="'Inter', sans-serif">Inter</option>
                  <option value="Arial, sans-serif">Arial</option>
                  <option value="'Helvetica Neue', Helvetica, sans-serif">
                    Helvetica
                  </option>
                  <option value="'Times New Roman', Times, serif">
                    Times New Roman
                  </option>
                  <option value="Georgia, serif">Georgia</option>
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Container Width
                </label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="number"
                    value={globalSettings.containerWidth}
                    onChange={(e) =>
                      updateGlobalSetting(
                        "containerWidth",
                        parseInt(e.target.value) || 600,
                      )
                    }
                    className="w-20 outline-none px-3 py-2 rounded-lg text-slate-900 bg-slate-50 border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                  />
                  <span className="text-xs text-slate-500 font-bold uppercase">
                    PX
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="self-stretch flex flex-row gap-8 w-full mt-4">
            <div className="flex-1 flex flex-col gap-4">
              <div className="flex justify-between items-center pb-2 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-800">
                  Email Architecture
                </h2>
                <div className="text-xs font-medium text-slate-500">
                  {blocks.length} Blocks
                </div>
              </div>

              <div className="flex flex-col gap-4 max-h-[650px] overflow-y-auto pr-2 pb-8 custom-scrollbar">
                {blocks.map((block, index) => (
                  <div
                    key={block.id}
                    className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm relative group hover:border-indigo-300 transition-colors"
                  >
                    <div className="flex justify-between items-center mb-4 border-b border-slate-100 pb-3">
                      <span className="text-indigo-600 font-bold uppercase text-xs tracking-wider flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                        {block.type === "split" ? "Split Row" : block.type}
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => moveBlock(index, "up")}
                          disabled={index === 0}
                          className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 transition-all"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M5 15l7-7 7 7"
                            ></path>
                          </svg>
                        </button>
                        <button
                          onClick={() => moveBlock(index, "down")}
                          disabled={index === blocks.length - 1}
                          className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 transition-all"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 9l-7 7-7-7"
                            ></path>
                          </svg>
                        </button>
                        <button
                          onClick={() => removeBlock(block.id)}
                          className="p-1.5 ml-1 rounded-md text-red-400 hover:bg-red-50 hover:text-red-600 transition-all"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M6 18L18 6M6 6l12 12"
                            ></path>
                          </svg>
                        </button>
                      </div>
                    </div>

                    {renderControlInputs(
                      block,
                      (key, val) => updateBlock(block.id, key, val),
                      block.id,
                      null,
                    )}

                    {block.type === "split" && (
                      <div className="flex gap-4 mt-2">
                        <div className="flex-1 bg-slate-50 p-3 rounded-lg border border-slate-200">
                          <div className="text-xs font-bold text-slate-500 uppercase mb-2">
                            Left Column
                          </div>
                          <select
                            value={block.left.type}
                            onChange={(e) =>
                              changeColumnType(block.id, "left", e.target.value)
                            }
                            className="w-full p-1.5 text-sm border border-slate-300 rounded mb-2 bg-white"
                          >
                            <option value="text">Text</option>
                            <option value="heading">Heading</option>
                            <option value="image">Image</option>
                            <option value="button">Button</option>
                            <option value="social">Social Links</option>
                            <option value="empty">Empty</option>
                          </select>
                          {renderControlInputs(
                            block.left,
                            (key, val) =>
                              updateColumnContent(block.id, "left", key, val),
                            block.id,
                            "left",
                          )}
                        </div>
                        <div className="flex-1 bg-slate-50 p-3 rounded-lg border border-slate-200">
                          <div className="text-xs font-bold text-slate-500 uppercase mb-2">
                            Right Column
                          </div>
                          <select
                            value={block.right.type}
                            onChange={(e) =>
                              changeColumnType(
                                block.id,
                                "right",
                                e.target.value,
                              )
                            }
                            className="w-full p-1.5 text-sm border border-slate-300 rounded mb-2 bg-white"
                          >
                            <option value="text">Text</option>
                            <option value="heading">Heading</option>
                            <option value="image">Image</option>
                            <option value="button">Button</option>
                            <option value="social">Social Links</option>
                            <option value="empty">Empty</option>
                          </select>
                          {renderControlInputs(
                            block.right,
                            (key, val) =>
                              updateColumnContent(block.id, "right", key, val),
                            block.id,
                            "right",
                          )}
                        </div>
                      </div>
                    )}

                    {renderBackgroundControls(block.id, block)}

                    <div className="flex gap-4 mt-4 pt-4 border-t border-slate-100">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500 uppercase font-bold">
                          Block Top Gap (px)
                        </span>
                        <input
                          type="number"
                          value={
                            block.paddingTop !== undefined
                              ? block.paddingTop
                              : 0
                          }
                          onChange={(e) =>
                            updateBlock(
                              block.id,
                              "paddingTop",
                              parseInt(e.target.value) || 0,
                            )
                          }
                          className="w-16 outline-none px-2 py-1 text-sm rounded text-slate-900 bg-slate-50 border border-slate-200 focus:border-indigo-500"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500 uppercase font-bold">
                          Block Bottom Gap (px)
                        </span>
                        <input
                          type="number"
                          value={
                            block.paddingBottom !== undefined
                              ? block.paddingBottom
                              : 0
                          }
                          onChange={(e) =>
                            updateBlock(
                              block.id,
                              "paddingBottom",
                              parseInt(e.target.value) || 0,
                            )
                          }
                          className="w-16 outline-none px-2 py-1 text-sm rounded text-slate-900 bg-slate-50 border border-slate-200 focus:border-indigo-500"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-4 gap-3 mt-2 bg-white p-3 rounded-xl border border-slate-200 shadow-sm">
                <button
                  onClick={() => addBlock("heading")}
                  className="flex items-center justify-center gap-1 bg-slate-50 border border-slate-200 text-slate-700 py-2.5 rounded-lg text-sm font-medium hover:bg-indigo-50 hover:text-indigo-700 transition-all"
                >
                  <span>+</span> Heading
                </button>
                <button
                  onClick={() => addBlock("text")}
                  className="flex items-center justify-center gap-1 bg-slate-50 border border-slate-200 text-slate-700 py-2.5 rounded-lg text-sm font-medium hover:bg-indigo-50 hover:text-indigo-700 transition-all"
                >
                  <span>+</span> Text
                </button>
                <button
                  onClick={() => addBlock("image")}
                  className="flex items-center justify-center gap-1 bg-slate-50 border border-slate-200 text-slate-700 py-2.5 rounded-lg text-sm font-medium hover:bg-indigo-50 hover:text-indigo-700 transition-all"
                >
                  <span>+</span> Image
                </button>
                <button
                  onClick={() => addBlock("button")}
                  className="flex items-center justify-center gap-1 bg-slate-50 border border-slate-200 text-slate-700 py-2.5 rounded-lg text-sm font-medium hover:bg-indigo-50 hover:text-indigo-700 transition-all"
                >
                  <span>+</span> Button
                </button>
                <button
                  onClick={() => addBlock("social")}
                  className="flex items-center justify-center gap-1 bg-slate-50 border border-slate-200 text-slate-700 py-2.5 rounded-lg text-sm font-medium hover:bg-indigo-50 hover:text-indigo-700 transition-all"
                >
                  <span>+</span> Social
                </button>
                <button
                  onClick={() => addBlock("split")}
                  className="flex items-center justify-center gap-1 bg-indigo-50 border border-indigo-200 text-indigo-700 py-2.5 rounded-lg text-sm font-medium hover:bg-indigo-100 transition-all col-span-2"
                >
                  <span>+</span> Split Row
                </button>
                <button
                  onClick={() => addBlock("divider")}
                  className="flex items-center justify-center gap-1 bg-slate-50 border border-slate-200 text-slate-700 py-2.5 rounded-lg text-sm font-medium hover:bg-indigo-50 hover:text-indigo-700 transition-all"
                >
                  <span>+</span> Divider
                </button>
              </div>
            </div>

            <div className="flex-1 flex flex-col gap-4">
              <div className="flex justify-between items-center pb-2 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-800">
                  Live Preview
                </h2>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                  <span className="text-xs font-medium text-slate-500">
                    Real-time sync
                  </span>
                </div>
              </div>

              <div className="w-full bg-black rounded-2xl overflow-hidden shadow-xl border-4 border-slate-800 h-[750px] relative">
                <iframe
                  srcDoc={liveHtmlPreview}
                  title="Email Preview"
                  className="w-full h-full border-none bg-black absolute inset-0"
                />
              </div>
            </div>
          </div>
        </>
      )}

      <div className="w-full flex justify-end mt-8 border-t border-slate-200 pt-6">
        <button
          onClick={sendEmail}
          disabled={isLoading}
          className={`cursor-pointer px-10 py-4 rounded-xl flex items-center justify-center shadow-md transition-all font-semibold text-lg ${
            isLoading
              ? "bg-slate-300 text-slate-500 cursor-not-allowed border-none"
              : "bg-indigo-600 hover:bg-indigo-700 text-white border border-indigo-700 hover:shadow-lg hover:-translate-y-0.5"
          }`}
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <svg
                className="animate-spin h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Dispatching...
            </span>
          ) : (
            "Dispatch Campaign"
          )}
        </button>
      </div>
    </section>
  );
};

export default CampaignBuilder;
